// Van Lease Statement — a branded PDF statement of the vehicle-lease charges that
// were applied to a driver's Recipient Created Tax Invoices (RCTIs) over a period.
//
// The data comes straight from the RCTI archive (getDriverRctis → each rcti's
// deductions.leaseFee), so the figures match exactly what the driver was charged.
// House style mirrors the Hollypaul Leasing vehicle-lease contract (black & gold)
// since Hollypaul Leasing is the entity that owns and leases the vans.
//
// buildLeaseStatementPdfBytes(data) → Uint8Array (A4 PDF). Pure/browser-testable
// (needs only pdf-lib). Reuses bytesToBase64 from leaseContract for the upload path.

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

export { bytesToBase64 } from './leaseContract';

const fmt = (n) => '$' + (Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtDate(d) {
  const dt = (d instanceof Date) ? d : new Date(d);
  if (isNaN(dt)) return String(d || '');
  return dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

// data: {
//   driverName, driverEntity, driverAbn,        // recipient
//   issuerName, issuerAbn,                       // Hollypaul Leasing
//   cutoffLabel,                                 // e.g. '30 June 2026'
//   generatedOn,                                 // Date | ISO
//   rows: [{ periodLabel, invoiceNumber, periodEndISO, leaseFee, leaseLabel }],
//   total,                                       // sum of leaseFee
//   coverageNote,                                // e.g. archive-start caveat
// }
export async function buildLeaseStatementPdfBytes(data) {
  const d = data || {};
  const rows = Array.isArray(d.rows) ? d.rows : [];
  const total = d.total != null ? Number(d.total) : rows.reduce((s, r) => s + (Number(r.leaseFee) || 0), 0);

  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  const PW = 595.28, PH = 841.89, M = 54, FOOT = 48;
  const GOLD = rgb(0.788, 0.659, 0.298);   // #C9A84C
  const INK = rgb(0.067, 0.067, 0.078);    // #111114
  const TEXT = rgb(0.12, 0.12, 0.12);
  const GREY = rgb(0.5, 0.5, 0.5);

  let page = pdf.addPage([PW, PH]);
  let y = 0;

  // ── Branded header band ──
  const BAND = 92;
  const header = () => {
    page.drawRectangle({ x: 0, y: PH - BAND, width: PW, height: BAND, color: INK });
    page.drawRectangle({ x: 0, y: PH - BAND - 3, width: PW, height: 3, color: GOLD });
    page.drawText('HOLLYPAUL', { x: M, y: PH - 46, size: 25, font: bold, color: GOLD });
    page.drawText('LEASING PTY LTD', { x: M, y: PH - 64, size: 9, font, color: rgb(0.85, 0.85, 0.9) });
    const hTitle = 'VAN LEASE STATEMENT';
    page.drawText(hTitle, { x: PW - M - bold.widthOfTextAtSize(hTitle, 13), y: PH - 44, size: 13, font: bold, color: rgb(1, 1, 1) });
    const hSub = `Prepared ${fmtDate(d.generatedOn || new Date())}`;
    page.drawText(hSub, { x: PW - M - font.widthOfTextAtSize(hSub, 8.5), y: PH - 60, size: 8.5, font, color: rgb(0.7, 0.7, 0.78) });
    y = PH - BAND - 26;
  };

  let pageNo = 0;
  const footer = () => {
    pageNo += 1;
    page.drawLine({ start: { x: M, y: 38 }, end: { x: PW - M, y: 38 }, thickness: 0.5, color: rgb(0.8, 0.8, 0.8) });
    page.drawText('HOLLYPAUL LEASING PTY LTD · Van Lease Statement', { x: M, y: 27, size: 7, font, color: GREY });
    const pn = `Page ${pageNo}`;
    page.drawText(pn, { x: PW - M - font.widthOfTextAtSize(pn, 7), y: 27, size: 7, font, color: GREY });
  };

  const newPage = () => { footer(); page = pdf.addPage([PW, PH]); pageNo === 0 ? header() : (y = PH - M); };
  header();

  const ensure = (need) => { if (y - need < FOOT + 14) newPage(); };

  const line = (text, { f = font, size = 9.5, color = TEXT, gap = 4, x = M } = {}) => {
    ensure(size + gap);
    page.drawText(String(text), { x, y: y - size, size, font: f, color });
    y -= size + gap;
  };

  // ── Recipient / issuer block ──
  line('STATEMENT FOR', { f: bold, size: 8.5, color: GOLD, gap: 6 });
  if (d.driverEntity) line(d.driverEntity, { f: bold, size: 11.5, gap: 3 });
  if (d.driverName && d.driverName !== d.driverEntity) line(`Attn: ${d.driverName}`, { size: 9.5, gap: 3 });
  if (d.driverAbn) line(`ABN ${d.driverAbn}`, { size: 9, color: GREY, gap: 3 });
  y -= 10;

  const intro = `This statement sets out the vehicle lease charges applied to your Recipient Created Tax Invoices up to and including ${d.cutoffLabel || fmtDate(d.generatedOn || new Date())}.`;
  // simple wrap
  const wrap = (text, f, size, maxW) => {
    const words = String(text).split(/\s+/); const out = []; let cur = '';
    for (const w of words) {
      const t = cur ? cur + ' ' + w : w;
      if (f.widthOfTextAtSize(t, size) > maxW && cur) { out.push(cur); cur = w; } else cur = t;
    }
    if (cur) out.push(cur); return out;
  };
  wrap(intro, font, 9.5, PW - M * 2).forEach(ln => line(ln, { size: 9.5, gap: 3 }));
  y -= 12;

  // ── Table ──
  const colX = { period: M, invoice: M + 210, end: M + 330, fee: PW - M };
  const th = (label, x, right) => page.drawText(label, {
    x: right ? x - bold.widthOfTextAtSize(label, 8) : x, y: y - 8, size: 8, font: bold, color: GOLD,
  });
  ensure(24);
  th('PERIOD', colX.period);
  th('INVOICE #', colX.invoice);
  th('PERIOD END', colX.end);
  th('LEASE FEE', colX.fee, true);
  y -= 12;
  page.drawLine({ start: { x: M, y }, end: { x: PW - M, y }, thickness: 0.8, color: GOLD });
  y -= 4;

  const rowH = 18;
  if (rows.length === 0) {
    line('No van lease charges were recorded on your RCTIs for this period.', { size: 9.5, color: GREY, gap: 6 });
  } else {
    rows.forEach((r, i) => {
      ensure(rowH);
      if (i % 2 === 1) page.drawRectangle({ x: M - 4, y: y - rowH + 5, width: PW - M * 2 + 8, height: rowH, color: rgb(0.965, 0.955, 0.925) });
      const yy = y - 12;
      page.drawText(String(r.periodLabel || ''), { x: colX.period, y: yy, size: 9, font, color: TEXT });
      page.drawText(String(r.invoiceNumber || '—'), { x: colX.invoice, y: yy, size: 9, font, color: TEXT });
      page.drawText(r.periodEndISO ? fmtDate(r.periodEndISO) : '—', { x: colX.end, y: yy, size: 9, font, color: TEXT });
      const fee = fmt(r.leaseFee);
      page.drawText(fee, { x: colX.fee - font.widthOfTextAtSize(fee, 9), y: yy, size: 9, font, color: TEXT });
      y -= rowH;
    });
  }

  // ── Total ──
  ensure(30);
  y -= 4;
  page.drawLine({ start: { x: M, y }, end: { x: PW - M, y }, thickness: 0.8, color: GOLD });
  y -= 6;
  page.drawText('Total van lease charges', { x: M, y: y - 11, size: 11, font: bold, color: INK });
  const tot = fmt(total);
  page.drawText(tot, { x: PW - M - bold.widthOfTextAtSize(tot, 12), y: y - 11, size: 12, font: bold, color: INK });
  y -= 24;

  // ── Notes ──
  y -= 8;
  const note = 'These lease charges are netted from your gross earnings before GST on each Recipient Created Tax Invoice, consistent with your RCTIs — this statement is a summary of amounts already invoiced, not a separate charge.';
  wrap(note, font, 8.5, PW - M * 2).forEach(ln => line(ln, { size: 8.5, color: GREY, gap: 2.5 }));
  if (d.coverageNote) {
    y -= 4;
    wrap(d.coverageNote, font, 8.5, PW - M * 2).forEach(ln => line(ln, { size: 8.5, color: GREY, gap: 2.5 }));
  }
  y -= 14;
  line(d.issuerName || 'Hollypaul Leasing Pty Ltd', { f: bold, size: 9.5, gap: 2.5 });
  if (d.issuerAbn) line(`ABN ${d.issuerAbn}`, { size: 8.5, color: GREY, gap: 2.5 });

  footer();
  return await pdf.save();
}
